/**
 * KioskBadgeScan — continuous wall-scanner overlay for the kiosk board
 * (Aug 2026). Workers hold their personal badge QR (payload
 * "HTBADGE:<token>") up to the kiosk camera to punch in/out. Stays open
 * for back-to-back scans; duplicate reads are debounced.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ScanLine, AlertTriangle, X } from 'lucide-react';

export default function KioskBadgeScan({ open, title, hint, closeLabel, errorMsg, onToken, onClose }) {
  const [cameraError, setCameraError] = useState(null);
  const scannerRef = useRef(null);
  const lastRef = useRef({ token: null, at: 0 });

  useEffect(() => {
    if (!open) return undefined;
    setCameraError(null);
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return;
        const instance = new Html5Qrcode('kiosk-badge-reader');
        scannerRef.current = instance;
        await instance.start(
          { facingMode: 'user' },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (text) => {
            const raw = String(text || '').trim();
            const m = raw.match(/HTBADGE:([A-Za-z0-9_-]+)/);
            if (!m) return;
            const tok = m[1];
            const now = Date.now();
            // Debounce: same badge within 8s, or any badge within 2.5s.
            if ((lastRef.current.token === tok && now - lastRef.current.at < 8000)
              || now - lastRef.current.at < 2500) return;
            lastRef.current = { token: tok, at: now };
            onToken?.(tok);
          },
          () => {},
        );
      } catch {
        if (!cancelled) setCameraError('Camera unavailable — allow camera access on this device.');
      }
    })();
    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      scannerRef.current = null;
      if (inst) inst.stop().then(() => inst.clear()).catch(() => {});
    };
  }, [open]); // eslint-disable-line

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4,10,20,0.92)', backdropFilter: 'blur(6px)' }}
      data-testid="kiosk-badge-scan-overlay"
    >
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#121D2E', border: '1px solid #2D3748' }}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-white text-lg font-semibold flex items-center gap-2">
            <ScanLine className="w-5 h-5" style={{ color: '#1E88E5' }} />
            {title}
          </p>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="p-2 rounded-lg hover:bg-white/10"
            style={{ color: '#94A3B8' }}
            data-testid="kiosk-badge-scan-close"
            aria-label="Close scanner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs mb-4" style={{ color: '#94A3B8' }}>{hint}</p>

        <div
          className="relative rounded-2xl overflow-hidden"
          style={{ background: '#000', border: '1px solid #2D3748', aspectRatio: '1/1' }}
        >
          <div id="kiosk-badge-reader" className="w-full h-full" data-testid="kiosk-badge-camera" />
          {cameraError && (
            <div
              className="absolute inset-0 flex items-start gap-2 p-4 text-sm"
              style={{ color: '#F0A020', background: '#0F1923' }}
              data-testid="kiosk-badge-camera-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{cameraError}</span>
            </div>
          )}
        </div>

        {errorMsg && (
          <p className="text-center text-sm mt-3 font-medium" style={{ color: '#EF4444' }} data-testid="kiosk-badge-error">
            {errorMsg}
          </p>
        )}

        <button
          type="button"
          onClick={() => onClose?.()}
          className="w-full mt-4 py-3 rounded-xl text-sm font-semibold"
          style={{ background: '#1A2433', color: '#94A3B8', border: '1px solid #2D3748' }}
          data-testid="kiosk-badge-scan-done"
        >
          {closeLabel}
        </button>
      </div>
    </div>
  );
}
